import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const KINDS = [
  { id: 'clients', label: 'Clients', help: 'Columns: name (req), legalName, nuis, vatNumber, email, phone, address, city, country, defaultLang, paymentTermsDays, notes' },
  { id: 'articles', label: 'Articles', help: 'Columns: sku (req), name (req), description, kind, price, unit, vatRate, stockQty, avgCost, categoryName, barcode' },
  { id: 'netlynk-invoices', label: 'Invoices (Netlynk format)', help: 'Your existing Netlynk_Invoice_Import.xlsx format — 1 row per invoice with up to 3 line items inline. Imports as SENT.' },
  { id: 'expenses', label: 'Expenses', help: 'Your existing Bridge4IP_Expenses_Import.xlsx format — date, vendor, description, amount, tax_amount, currency, category, notes' },
  { id: 'payments', label: 'Payments', help: 'Columns: date, clientName, amount, method, currency, reference, notes, allocateInvoice (invoice number)' },
];

export default function ImportPage() {
  const [sp] = useSearchParams();
  const [kind, setKind] = useState(sp.get('kind') || 'clients');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [commitResult, setCommitResult] = useState(null);

  const submit = async (mode) => {
    setBusy(true); setErr(''); setCommitResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`/api/import/${kind}/${mode}`, { method: 'POST', body: fd, credentials: 'include' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'failed');
      if (mode === 'preview') setPreview(data);
      else setCommitResult(data);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const info = KINDS.find(k => k.id === kind);

  return (
    <>
      <div className="page-header"><h1>Bulk import</h1></div>
      <div className="card">
        <div className="row">
          <div className="field" style={{ flex: 2 }}>
            <label>What are you importing?</label>
            <select value={kind} onChange={e => { setKind(e.target.value); setPreview(null); setCommitResult(null); }}>
              {KINDS.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 3 }}>
            <label>XLSX or CSV file</label>
            <input type="file" accept=".xlsx,.xls,.csv" onChange={e => { setFile(e.target.files[0]); setPreview(null); setCommitResult(null); }} />
          </div>
        </div>
        <div className="muted" style={{ fontSize: 12 }}>{info.help}</div>
        {err && <div className="error-banner mt-8">{err}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn btn-secondary" disabled={!file || busy} onClick={() => submit('preview')}>{busy ? <span className="spinner" /> : 'Preview'}</button>
          <button className="btn btn-primary" disabled={!preview || busy} onClick={() => submit('commit')}>{busy ? <span className="spinner" /> : 'Commit import'}</button>
        </div>
      </div>

      {commitResult && (
        <div className="card">
          <h2>Import complete</h2>
          <pre style={{ background: 'var(--sky-pale)', padding: 12, borderRadius: 8 }}>{JSON.stringify(commitResult.summary, null, 2)}</pre>
          {commitResult.errors?.length > 0 && (
            <details><summary>{commitResult.errors.length} errors</summary>
              <pre>{JSON.stringify(commitResult.errors, null, 2)}</pre>
            </details>
          )}
        </div>
      )}

      {preview && (
        <div className="card">
          <h2>Preview</h2>
          <div className="muted">{JSON.stringify(preview.summary)}</div>
          {preview.errors?.length > 0 && (
            <div className="error-banner">
              {preview.errors.length} rows had errors — they will be skipped on commit.
              <details><summary>Details</summary><pre>{JSON.stringify(preview.errors.slice(0, 20), null, 2)}</pre></details>
            </div>
          )}
          <h3>First 10 rows</h3>
          <pre style={{ maxHeight: 400, overflow: 'auto', background: 'var(--ivory)', padding: 12, borderRadius: 8, fontSize: 12 }}>
            {JSON.stringify(preview.rows?.slice(0, 10), null, 2)}
          </pre>
        </div>
      )}
    </>
  );
}
